import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BACKEND_URL } from '../app.config';
import { WorkflowTemplate, CreateWorkflowTemplateDto, WorkflowTemplateStep, WorkflowTemplateStepAction, CreateStepDto, UpdateStepDto, CreateActionDto, UpdateActionDto, ActionSaveResultDto, ActionDataSendEmail, ActionDataUpdateData, ActionDataUpdateProgress, ActionDataUpdateStatus, Matter } from '../api/data-model';
import { ActionType, MatterStatus } from '../api/enum';
import { ListResult } from '../api/core/list-result';
import { ProcessTemplateByForm } from '../api/process/process-template';

@Injectable()
export class WorkflowTemplateService {
  constructor(private readonly http: HttpClient, @Inject(BACKEND_URL) private readonly baseUrl: string) { }

  // Helper method to deserialize action data
  private deserializeActionData(action: WorkflowTemplateStepAction): any {
    if (!action.data || action.data.trim() === '') {
      return null;
    }

    try {
      return JSON.parse(action.data);
    } catch (error) {
      console.error('Failed to parse action data:', error, action);
      return null;
    }
  }

  // Helper method to deserialize and enrich actions
  private deserializeActions(actions: WorkflowTemplateStepAction[]): WorkflowTemplateStepAction[] {
    return actions.map(action => {
      const parsedData = this.deserializeActionData(action);

      // Generate basic additionalInfo (without external data dependencies)
      let additionalInfo = action.additionalInfo;

      if (parsedData) {
        switch (action.type) {
          case ActionType['Send Email']:
            // Service layer doesn't process, leave for Component layer to lookup emailTemplate
            additionalInfo = additionalInfo || 'Send email';
            break;

          case ActionType['Update Status']:
            const statusData = parsedData as ActionDataUpdateStatus;
            additionalInfo = `Set status to: ${MatterStatus[statusData.status] || 'Unknown'}`;
            break;

          case ActionType['Update Data']:
            const updateData = parsedData as ActionDataUpdateData;
            const fieldCount = Object.keys(updateData.fieldValues || {}).length;
            additionalInfo = `Update ${fieldCount} field(s)`;
            break;

          case ActionType['Update Receipt']:
          case ActionType['Update Priority Date']:
            additionalInfo = ActionType[action.type];
            break;

          // Update Progress Status will be handled in component with progressStatuses
          case ActionType['Update Progress Status']:
            // Keep original or set placeholder
            additionalInfo = additionalInfo || 'Progress status update';
            break;

          case ActionType['Create Task']:
            additionalInfo = additionalInfo || 'Create task';
            break;
        }
      }

      return {
        ...action,
        parsedData,
        additionalInfo: additionalInfo || '-'
      };
    });
  }

  getList(): Observable<WorkflowTemplate[]> {
    return this.http.get<WorkflowTemplate[]>(this.baseUrl + '/api/app/process-template/simple-list');
  }

  getByForm(formId: string): Observable<ProcessTemplateByForm[]> {
    return this.http.get<ProcessTemplateByForm[]>(
      `${this.baseUrl}/api/app/process-template/by-form/${formId}`
    );
  }

  getProcessModule(id: string): Observable<WorkflowTemplate> {
    return this.http.get<WorkflowTemplate>(this.baseUrl + '/api/app/process-template/' + id);
  }

  setActive(id: string, isActive: boolean): Observable<void> {
    const params = new HttpParams().set('isActive', isActive.toString());
    return this.http.post<void>(this.baseUrl + '/api/app/process-template/' + id + '/set-active', null, { params });
  }

  getDetails(id: string): Observable<WorkflowTemplate> {
    return this.http.get<WorkflowTemplate>(this.baseUrl + '/api/app/process-template/' + id + '/detail').pipe(
      map(template => ({
        ...template,
        steps: template.steps?.map(step => ({
          ...step,
          actions: this.deserializeActions(step.actions || [])
        })) || []
      }))
    );
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(this.baseUrl + '/api/app/process-template/' + id);
  }

  create(dto: CreateWorkflowTemplateDto): Observable<WorkflowTemplate> {
    return this.http.post<WorkflowTemplate>(this.baseUrl + '/api/app/process-template', dto);
  }

  updateBasicInfo(id: string, dto: CreateWorkflowTemplateDto): Observable<WorkflowTemplate> {
    return this.http.put<WorkflowTemplate>(
      `${this.baseUrl}/api/app/process-template/${id}/basic-info`,
      dto
    );
  }

  updateTaskListId(templateId: string, taskListId: string | null): Observable<WorkflowTemplate> {
    return this.http.put<WorkflowTemplate>(
      `${this.baseUrl}/api/app/process-template/${templateId}/task-list`,
      { taskListId }
    );
  }

  updateForms(
    templateId: string,
    forms: { formId: string; category: number }[]
  ): Observable<WorkflowTemplate> {
    return this.http.put<WorkflowTemplate>(
      `${this.baseUrl}/api/app/process-template/${templateId}/forms`,
      { forms }
    );
  }

  updateLetters(templateId: string, letterIds: string[]): Observable<WorkflowTemplate> {
    return this.http.put<WorkflowTemplate>(
      `${this.baseUrl}/api/app/process-template/${templateId}/letters`,
      { letterIds }
    );
  }

  updateQuestionnaires(
    templateId: string,
    questionnaires: { questionnaireId: string; category: number }[]
  ): Observable<WorkflowTemplate> {
    return this.http.put<WorkflowTemplate>(
      `${this.baseUrl}/api/app/process-template/${templateId}/questionnaires`,
      { questionnaires }
    );
  }

  updateDocRequests(templateId: string, docRequests: Array<{ docRequestId: string; audience: number; required: boolean }>): Observable<WorkflowTemplate> {
    return this.http.put<WorkflowTemplate>(
      `${this.baseUrl}/api/app/process-template/${templateId}/doc-requests`,
      { docRequests }
    );
  }

  // Step CRUD operations
  createStep(templateId: string, step: CreateStepDto): Observable<WorkflowTemplateStep[]> {
    return this.http.post<WorkflowTemplateStep[]>(
      `${this.baseUrl}/api/app/process-template/${templateId}/steps`,
      step
    ).pipe(
      map(steps => steps.map(s => ({
        ...s,
        actions: this.deserializeActions(s.actions || [])
      })))
    );
  }

  updateStep(templateId: string, stepId: string, step: UpdateStepDto): Observable<WorkflowTemplateStep[]> {
    return this.http.put<WorkflowTemplateStep[]>(
      `${this.baseUrl}/api/app/process-template/${templateId}/steps/${stepId}`,
      step
    ).pipe(
      map(steps => steps.map(s => ({
        ...s,
        actions: this.deserializeActions(s.actions || [])
      })))
    );
  }

  deleteStep(templateId: string, stepId: string): Observable<WorkflowTemplateStep[]> {
    return this.http.delete<WorkflowTemplateStep[]>(
      `${this.baseUrl}/api/app/process-template/${templateId}/steps/${stepId}`
    ).pipe(
      map(steps => steps.map(s => ({
        ...s,
        actions: this.deserializeActions(s.actions || [])
      })))
    );
  }

  // Action CRUD operations
  createAction(templateId: string, stepId: string, action: CreateActionDto): Observable<ActionSaveResultDto> {
    return this.http.post<ActionSaveResultDto>(
      `${this.baseUrl}/api/app/process-template/${templateId}/steps/${stepId}/actions`,
      action
    ).pipe(
      map(result => ({
        actions: this.deserializeActions(result?.actions || []),
        warnings: result?.warnings || [],
      }))
    );
  }

  updateAction(templateId: string, actionId: string, action: UpdateActionDto): Observable<ActionSaveResultDto> {
    return this.http.put<ActionSaveResultDto>(
      `${this.baseUrl}/api/app/process-template/${templateId}/actions/${actionId}`,
      action
    ).pipe(
      map(result => ({
        actions: this.deserializeActions(result?.actions || []),
        warnings: result?.warnings || [],
      }))
    );
  }

  deleteAction(templateId: string, actionId: string): Observable<WorkflowTemplateStepAction[]> {
    return this.http.delete<WorkflowTemplateStepAction[]>(
      `${this.baseUrl}/api/app/process-template/${templateId}/actions/${actionId}`
    ).pipe(
      map(actions => this.deserializeActions(actions))
    );
  }

  // Get related matters for a workflow template
  getRelatedMatters(templateId: string, skipCount: number, maxResultCount: number): Observable<ListResult<Matter>> {
    const params = new HttpParams()
      .set('Sorting', 'lastModificationTime desc')
      .set('SkipCount', skipCount.toString())
      .set('MaxResultCount', maxResultCount.toString());

    return this.http.get<ListResult<Matter>>(
      `${this.baseUrl}/api/app/process-template/${templateId}/matters`,
      { params }
    );
  }
}
